import { HttpStatusCode } from "axios";
import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import { formatPostTitle } from "../../../../libs/talk";
import { safeReplaceAll } from "../../../../libs/replace";

import {
  getTalkDetail,
  TalkDetail
} from "../../../../services/talkServices";
import { useUserContext } from "../../../../contexts/userContext";

import useFullScreenLoading from "../../../../hooks/useFullScreenLoading";
import useSimplePopup from "../../../../hooks/useSimplePopup";

import StickyHeader from "../../../../components/Layout/StickyHeader";
import HorizontalRule from "../../../../components/Rule/Horizontal";
import UserProfile from "../../../../components/UserProfile";
import PinchZoomImage from "../../../../components/PinchZoomImage";

type Props = {};

export default function FaqDetailPage(props: Props) {
  const navigate = useNavigate();
  const params = useParams();
  const location = useLocation();
  const [detail, setDetail] = useState<Partial<TalkDetail>>({});
  const [user] = useUserContext();
  const [isLoading, setIsLoading] = useState(true);
  const loadingComponent = useFullScreenLoading({ isLoading });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [image, setImage] = useState<File | string>("");
  const [popupComponent, { open }] = useSimplePopup({});
  const containerRef = useRef<HTMLDivElement>(null);
  const [replaceContent, setReplaceContent] = useState("");

  const loadDetail = async () => {
    if (!params.id) return;
    try {
      const { status, data } = await getTalkDetail(params.id);
      if (status === HttpStatusCode.Ok) {
        setDetail((prev) => ({ ...prev, ...data }));
        setReplaceContent(replaceLinks(data?.content));
      }
    } catch (e: any) {
      if (e?.response?.data?.error) {
        open(e.response.data.error?.replace(". ", ".\n"));
      } else {
        open("게시글을 불러오는데 실패했습니다.");
      }
    }
  };

  function replaceLinks(inputString?: string) {
    if (!inputString) return "";
    const plainString = new DOMParser().parseFromString(
      inputString,
      "text/html"
    );

    // 정규식을 사용하여 링크를 찾고 치환
    const linkRegex = /(?:https?:\/\/|www\.)\S+/g;
    const replacedString = safeReplaceAll(
      plainString.body.textContent?.replace(linkRegex, (match) => {
        // 링크가 http://, https://, www로 시작하지 않으면 http://를 추가하여 완전한 URL로 만듦
        const url =
          match.startsWith("http://") || match.startsWith("https://")
            ? match
            : "http://" + match;

        return `<a href="${url}" target="_blank" class="text-blue-400">${match}</a>`;
      }) ?? "",
      "\n",
      "<br/>"
    );

    return replacedString || "";
  }

  useEffect(() => {
    if (user.isLoading) return;
    setIsLoading(true);
    loadDetail().finally(() => setIsLoading(false));
  }, [params.id, user.isLoading]);

  useEffect(() => {
    const linkElements = containerRef.current?.querySelectorAll(".custom-link");

    linkElements?.forEach((linkElement) => {
      linkElement.addEventListener("click", (e) => {
        e.preventDefault();
        window.open(linkElement?.textContent ?? "", "_blank");
      });
    });

    return () => {
      linkElements?.forEach((linkElement) => {
        linkElement.removeEventListener("click", (e) => {
          e.preventDefault();
          window.open(linkElement?.textContent ?? "", "_blank");
        });
      });
    };
  }, [containerRef?.current, replaceContent]);

  return (
    <div className="">
      {loadingComponent}
      <StickyHeader
        title="샌디 FAQ"
        onBackClick={() => navigate(location?.state?.parentUrl ?? "/myinfo/faq")}
        className="px-4"
      />
      <div className="p-4 flex flex-col gap-4">
        <div>
          <UserProfile
            isFaq={true}
            nickname={detail.nickname ?? ""}
            createdAt={detail.createdAt ?? ""}
            modifiedAt={detail.modifiedAt ?? ""}
            profile={
              detail.memberFileInfo?.items.find(
                (item) => item.scale === "small"
              )?.url
            }
            role={detail.userRole}
          />
        </div>
        <HorizontalRule className="bg-black" margin="0" />

        {image && (
          <PinchZoomImage
            imageList={
              detail.fileInfo?.items
                ?.filter((file) => file.scale === "full")
                .map((file) => file.url) ?? []
            }
            isOpen={isModalOpen}
            onClose={(e: React.MouseEvent) => {
              e.stopPropagation();
              setIsModalOpen(false);
            }}
          />
        )}

        <div className="flex flex-col gap-4">
          {formatPostTitle(detail.title) && (
            <div className="text-xl font-semibold">
              {formatPostTitle(detail.title)}
            </div>
          )}
          <div className="flex flex-col gap-[1px]">
            {detail.fileInfo?.items
              ?.filter((file) => file.scale === "full")
              .map((file, index) => (
                <img
                  key={index}
                  src={file?.url}
                  alt={`${index}`}
                  onClick={() => {
                    setImage(file.url);
                    setIsModalOpen(true);
                  }}
                />
              ))}
          </div>
          <div
            className="text-sm text-brand-neutral-70 break-words"
            dangerouslySetInnerHTML={{
              __html: replaceContent,
            }}
            ref={containerRef}
          />
        </div>
      </div>
      <HorizontalRule thickness={6} margin="0" />
      {popupComponent}
    </div>
  );
}

export const ThreeDotIcon = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path d="M4.5 12H7.5" stroke="#1D1929" strokeWidth="3" />
    <path d="M10.5 12H13.5" stroke="#1D1929" strokeWidth="3" />
    <path d="M16.5 12H19.5" stroke="#1D1929" strokeWidth="3" />
  </svg>
);
